<template>
    <div className="input-wrapper">
        <input
            :id="name"
            :class="['text-input', value && 'filled']"
            :maxlength="maxLength"
            :type="type"
            v-model="maskedInput"
            @input="showLastCharacter"
            @focus="setPlaceholder(true)"
            @focusout="setPlaceholder(false)"
            :placeholder="placeholder"
            ref="inputField"
            @keydown.delete="duplicateDelete"
            @paste="duplicatePaste"
        />

        <FloatingLabel :label="displayLabel" :icon="icon" />

        <InputIcon
            v-show="placeholder || value"
            :icon="inputIcon"
            :key="showInput ? 'visible' : 'hidden'"
            @click="setInputVisibility()"
        />

        <div className="error" v-if="displayErrorMessage">{{ displayErrorMessage }}</div>

        <!-- must be a text field, NOT hidden or maska will break on Safari -->
        <input class="d-none" :name="name" type="text" v-model="value" v-maska :data-maska="mask" />
    </div>
</template>

<script>
import { useField } from 'vee-validate'
import { titleCase } from '@js/utilities/language.js'
import { useStore } from 'vuex'

// lodash components
import _get from 'lodash/get'
import { Mask } from 'maska'

export default {
    props: {
        customErrorMessage: String,
        icon: String,
        label: String,
        name: String,
        validationRules: Object,
        vuexKey: String,
        type: {
            type: String,
            default: 'input',
        },
    },
    data() {
        return {
            showPlaceholder: false,
            showInput: false,
            maskedInput: '',
            maskedInputMask: '',
            validateOnChange: false,
        }
    },
    setup(props) {
        const store = useStore()
        const { errorMessage, handleChange, resetField } = useField(props.name, props.validationRules, {
            label: titleCase(props.name),
        })

        return {
            errorMessage,
            handleChange,
            resetField,
            useField,
            store,
        }
    },
    mounted() {
        this.resetField({
            value: this.value,
        })

        if (this.value) {
            const maskValue = new Mask({ mask: this.mask })
            this.maskMaskedInput(maskValue.unmasked(this.value).length)
        }

        this.emitter.on('inputField:resetField', () => {
            this.resetField({
                value: this.value,
            })
        })
    },
    computed: {
        value: {
            get() {
                return this.store.getters.fieldValue(this.name, this.vuexKey || 'data', !!this.vuexKey)
            },
            set(value) {
                if (this.vuexKey) {
                    this.store.commit('setDataFieldValue', {
                        name: this.name,
                        value: value,
                        key: this.vuexKey,
                    })
                } else {
                    this.store.dispatch('setFieldValue', {
                        name: this.name,
                        value: value,
                    })
                }

                this.$emit('input', {
                    name: this.name,
                    value: value,
                })

                if (!this.validateOnChange && value.length >= this.maxLengthValue) {
                    this.validateOnChange = true
                }

                if (this.validateOnChange) {
                    // only validate in realtime when completed the full value at least once
                    this.handleChange(value)
                }
            },
        },
        mask() {
            return this.store.getters.field(this.name, this.vuexKey || 'data', !!this.vuexKey)?.mask
        },
        displayLabel() {
            return this.label || this.titleCase(this.name)
        },
        maxLength() {
            if (this.validationRules && this.validationRules.max) {
                return this.validationRules.max
            } else if (this.validationRules && this.validationRules.digits) {
                return this.validationRules.digits
            }

            return this?.mask?.length
        },
        // the length without the dahses
        maxLengthValue() {
            return this.maxLength - (this.mask?.split('-').length  - 1)
        },
        serverError() {
            return this.store.getters.fieldServerError(this.name)
        },
        displayErrorMessage() {
            return this.errorMessage ? this.interpretErrorMessage(this.errorMessage) : this.serverError
        },
        placeholder() {
            return this.showPlaceholder
                ? this.store.getters.field(this.name, this.vuexKey || 'data', !!this.vuexKey)?.placeholder
                : null
        },
        inputIcon() {
            return this.showInput ? 'fa fa-eye' : 'fa fa-eye-slash'
        },
    },
    methods: {
        interpretErrorMessage(errorString) {
            if (this.customErrorMessage) {
                return this.customErrorMessage
            }

            const defaultErrorStr = this.$t('errors.fieldNetworkError')

            const parts = errorString.split(' ')
            if (parts.length !== 2) {
                return defaultErrorStr
            }

            const ruleError = parts[0]

            if (ruleError === 'error') {
                return defaultErrorStr
            }
            return this.$t('errors.fieldGenericError')
        },
        focus() {
            this.autoFocus(this.$refs.inputField)
        },
        setPlaceholder(show) {
            this.showPlaceholder = show
        },
        setInputVisibility() {
            this.showInput = !this.showInput
            this.toggleMask(true)
        },
        showLastCharacter(event) {
            const lastCharacter = event.data
            const inputElement = event.target
            const start = inputElement.selectionStart
            const end = inputElement.selectionEnd

            if (!this.showInput) {
                this.value = [this.value?.slice(0, start) ?? '', lastCharacter, this.value?.slice(end) ?? ''].join('')
            } else {
                this.value = this.maskedInput
            }

            this.toggleMask()
        },
        toggleMask(toggle = false) {
            const maskValue = new Mask({ mask: this.mask })

            if (this.value && !this.showInput) {
                let value = maskValue.unmasked(this.value)
                let length = value.length - 1
                let lastValueCharacter = value?.charAt(length)

                if (!toggle) {
                    this.maskMaskedInput(length, lastValueCharacter)

                    setTimeout(() => {
                        this.maskMaskedInput(length + 1)
                    }, 500)
                } else {
                    this.maskMaskedInput(length + 1)
                }
            } else {
                this.maskedInput = this.value ? maskValue.masked(this.value) : ''
            }
        },
        replaceHashes(numberOfChar) {
            let count = 0
            return this.mask.replace(/#/g, match => {
                count++
                return count <= numberOfChar ? 'H' : match
            })
        },
        maskMaskedInput(length, lastValueCharacter = '') {
            const maskValue = new Mask({ mask: this.mask })
            let value = this.value ? maskValue.unmasked(this.value) : ''

            if (!lastValueCharacter && length !== value.length) {
                return
            }

            this.maskedInputMask = this.replaceHashes(length)
            const mask = new Mask({ mask: this.maskedInputMask, tokens: { H: { pattern: /X/ } } })

            this.maskedInput = mask.masked('X'.repeat(length) + lastValueCharacter)
        },
        duplicateDelete(event) {
            const inputElement = event.target
            let start = inputElement.selectionStart
            let end = inputElement.selectionEnd

            if (start === end && start !== 0) {
                start--
            }

            this.value = this.value?.slice(0, start) + this.value?.slice(end)
        },
        duplicatePaste(event) {
            const pasteData = event.clipboardData.getData('text')
            const inputElement = event.target
            const start = inputElement.selectionStart
            const end = inputElement.selectionEnd

            this.value = [this.value?.slice(0, start) ?? '', pasteData, this.value?.slice(end) ?? ''].join('')
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';

.input-wrapper {
    position: relative;

    input {
        &:focus,
        &.filled {
            padding-left: 10px;
            padding-top: 20px;
        }
    }

    .text-input {
        background: #ffffff;
        border-radius: 4px;
        border: 1px solid #e0e1e5;
        color: var(--input-text-color);
        font-size: 16px;
        height: 53px;
        position: relative;
        width: 100%;
    }

    .floating-label {
        color: var(--input-text-color);
        font-family: var(--secondary-font);
        font-size: 16px;
        left: 10px;
        pointer-events: none;
        position: absolute;
        top: 14px;
        transition: 0.2s ease all;
        text-overflow: ellipsis;
        max-width: 300px;
        overflow: hidden;

        @media screen and (min-width: $bp-large) {
            max-width: 380px;
        }

        .icon {
            color: black;
            font-size: 20px;
            vertical-align: middle;
        }

        .fa-stack {
            color: black;
            font-size: 12px;
            vertical-align: bottom;

            .icon {
                font-size: 11px;
            }
        }
    }

    .text-input-icon {
        color: var(--input-text-color);
        font-family: var(--secondary-font);
        font-size: 16px;
        left: 90%;
        position: absolute;
        top: 15px;
        transition: 0.2s ease all;
        text-overflow: ellipsis;
        max-width: 300px;
        overflow: hidden;
        cursor: pointer;

        @media screen and (min-width: $bp-large) {
            max-width: 380px;
        }

        @media screen and (max-width: 16em) {
            left: 85%;
        }

        .icon {
            color: black;
            font-size: 20px;
            vertical-align: middle;
        }

        .fa-stack {
            color: black;
            font-size: 12px;
            vertical-align: bottom;

            .icon {
                font-size: 11px;
            }
        }
    }
}
</style>
